/*
 * VN — Latest articles consumer.
 *
 * Reads the WordPress feed and fills the section that shipped with it. Three
 * rules shape everything below.
 *
 * 1. NEVER BREAK THE STOREFRONT. Every failure path — network, timeout, bad
 *    status, malformed JSON, empty list, unexpected shape — ends the same way:
 *    the section removes itself and the page carries on. There is no error
 *    state, because a shopper has no use for one.
 *
 * 2. NEVER TRUST THE PAYLOAD AS MARKUP. Every string goes in through
 *    textContent and every URL is checked against the blog's own origin before
 *    it reaches an href or a src. The feed is ours today; treating it as
 *    untrusted is what keeps a future compromise of it from becoming a
 *    compromise of the store.
 *
 * 3. DON'T RE-ASK WHAT WE ALREADY KNOW. sessionStorage holds the last good
 *    payload for a few minutes, so moving between pages is free. It sits in
 *    front of the endpoint's own Cache-Control, which is what stops most
 *    requests reaching WordPress at all.
 */
(function () {
  'use strict';

  var CACHE_PREFIX = 'vn-latest:';
  var CACHE_TTL_MS = 5 * 60 * 1000;
  var TIMEOUT_MS = 6000;

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.t !== 'number') return null;
      if (Date.now() - entry.t > CACHE_TTL_MS) return null;
      return entry.d;
    } catch (e) {
      return null; // private mode, quota, corrupted value — all just "no cache"
    }
  }

  function writeCache(key, data) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), d: data }));
    } catch (e) {
      /* A cache that cannot be written is not a reason to fail. */
    }
  }

  /** Same-origin-as-the-blog, https only. Anything else is not rendered. */
  function safeUrl(value, blogHost) {
    if (typeof value !== 'string' || !value) return null;
    try {
      var u = new URL(value, blogHost);
      if (u.protocol !== 'https:') return null;
      if (u.origin !== new URL(blogHost).origin) return null;
      return u.href;
    } catch (e) {
      return null;
    }
  }

  function formatDate(iso) {
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(document.documentElement.lang || 'en-GB', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch (e) {
      return '';
    }
  }

  /*
   * Keep the language app off a node. It walks the DOM and swaps any text it
   * recognises, which translated "Health & Wellbeing" into the store's own
   * menu wording while leaving "Sports & Outdoors" in English — one row, two
   * languages. Marking the section was not enough; the app checks the element
   * it is about to touch, so the mark has to be on that element.
   *
   * Every card links to an article written in English, so a translated label
   * would promise something the destination does not deliver.
   */
  function keepOriginalLanguage(el) {
    el.setAttribute('translate', 'no');
    el.className += ' notranslate';
    return el;
  }

  /*
   * Break a string for the language app without changing what a reader sees.
   *
   * The app matches whole phrases against its glossary, and it reaches further
   * than the standard opt-outs: it rewrote the kicker when it was a text node,
   * and rewrote it again when it was a data attribute painted by CSS. It
   * cannot match what is not a contiguous phrase, so a zero-width space goes
   * in after the first word. U+200B has no width and no line-breaking effect,
   * so "Health & Wellbeing" still reads as "Health & Wellbeing".
   *
   * This is a workaround for an app we do not control. The durable fix is an
   * exclusion rule in the app's own settings; there is no such setting in the
   * theme, so it is the Owner's to add if it ever appears.
   */
  function resistTranslation(text) {
    if (typeof text !== 'string') return '';
    var at = text.indexOf(' ');
    if (at === -1) return text;
    return text.slice(0, at + 1) + '​' + text.slice(at + 1);
  }

  /*
   * Write the hub label somewhere the language app cannot reach.
   *
   * It defeated every documented opt-out in turn — translate="no", the
   * .notranslate class, a CSS-painted data attribute, and a zero-width space
   * inside that attribute, which it normalised away before matching. What it
   * does not do is descend into a shadow root, so the label lives in one.
   *
   * Colour, font, weight, letter-spacing and text-transform are all inherited
   * CSS properties, so they cross the shadow boundary on their own and the
   * kicker still looks exactly like the rest of the card.
   *
   * If the browser has no shadow DOM, fall back to plain text: an occasional
   * translated label beats a card with no label at all.
   */
  function putLabel(el, text) {
    try {
      var root = el.attachShadow({ mode: 'open' });
      root.appendChild(document.createTextNode(text));
    } catch (e) {
      el.textContent = resistTranslation(text);
    }
  }

  function buildCard(item, blogHost, compact, opts) {
    var href = safeUrl(item && item.url, blogHost);
    var title = item && typeof item.title === 'string' ? item.title.trim() : '';
    if (!href || !title) return null; // a card with no destination is not a card

    var li = document.createElement('li');
    li.className = 'vn-latest__card';

    var a = document.createElement('a');
    a.className = 'vn-latest__link';
    a.href = href;
    // First-party cross-domain navigation: no UTMs, no rel=noopener theatre.

    var imgUrl = item.image ? safeUrl(item.image.url, blogHost) : null;
    if (imgUrl) {
      var media = document.createElement('div');
      media.className = 'vn-latest__media';
      var img = document.createElement('img');
      img.src = imgUrl;
      img.alt = typeof item.image.alt === 'string' ? item.image.alt : '';
      // Dimensions come from the feed so the browser reserves the box itself.
      if (item.image.width) img.width = item.image.width;
      if (item.image.height) img.height = item.image.height;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
      a.appendChild(media);
      /*
       * The side-by-side layouts put the image left and the text right. That
       * only makes sense when there IS an image: without this class the same
       * rule laid the kicker, the headline and the date out as three columns,
       * each wrapping mid-word. CSS `:has()` would express it, but a class the
       * builder already knows to add needs no browser support.
       */
      a.className += ' vn-latest__link--has-media';
    }
    /*
     * No placeholder when there is no image. Today none of the published posts
     * carries a featured image, and a row of identical grey rectangles reads as
     * broken; a clean text card reads as deliberate.
     */

    // Everything that is not the image lives in one column, always.
    var body = document.createElement('div');
    body.className = 'vn-latest__body';

    if (item.hub_label) {
      /*
       * The hub name is painted by CSS from a data attribute, not written as
       * text. The language app rewrites text nodes and ignores both
       * translate="no" and .notranslate, so the only way to keep the label in
       * the language of the article it points at is to leave no text node for
       * it to find. `content: attr(data-label)` is not part of the document
       * text, so it survives.
       *
       * aria-label carries the same string for assistive tech, which does not
       * read generated content reliably.
       */
      var kicker = document.createElement('span');
      kicker.className = 'vn-latest__kicker';
      kicker.setAttribute('aria-label', item.hub_label);
      kicker.setAttribute('role', 'text');
      keepOriginalLanguage(kicker);
      putLabel(kicker, item.hub_label);
      body.appendChild(kicker);
    }

    var h3 = document.createElement('h3');
    h3.className = 'vn-latest__headline';
    h3.textContent = title;
    keepOriginalLanguage(h3);
    body.appendChild(h3);

    if (!compact && item.excerpt) {
      var p = document.createElement('p');
      p.className = 'vn-latest__excerpt';
      p.textContent = item.excerpt;
      body.appendChild(p);
    }

    var when = formatDate(item.published_at);
    if (when) {
      var time = document.createElement('time');
      time.className = 'vn-latest__date';
      time.dateTime = item.published_at;
      time.textContent = when;

      /*
       * The sidebar reproduces the card the native blog widget drew, which put
       * a calendar glyph before the date and a byline after it, both inside
       * `.article_info`. Reusing the theme's own classes means the theme's own
       * spacing and colour apply — the same reason the heading borrows
       * `.home-title` rather than restating it.
       */
      if (opts && opts.nativeMeta) {
        var info = document.createElement('div');
        info.className = 'article_info';

        var dateWrap = document.createElement('span');
        dateWrap.className = 'article_info__date';
        dateWrap.appendChild(icon('fa-calendar'));
        dateWrap.appendChild(document.createTextNode(' '));
        dateWrap.appendChild(time);
        info.appendChild(dateWrap);

        if (opts.showAuthor && item.author) {
          var authorWrap = document.createElement('span');
          authorWrap.className = 'article_info__author';
          authorWrap.appendChild(icon('fa-user-secret'));
          authorWrap.appendChild(document.createTextNode(' '));
          var by = document.createElement('span');
          by.style.color = 'gray';
          by.textContent = 'by ';
          authorWrap.appendChild(by);
          authorWrap.appendChild(document.createTextNode(item.author));
          info.appendChild(authorWrap);
        }

        body.appendChild(info);
      } else {
        /*
         * The horizontal row has no icons and no theme classes — it is its own
         * design. The byline joins the date inside one muted line rather than
         * arriving as a second block, so the card keeps its shape.
         */
        var meta = document.createElement('div');
        meta.className = 'vn-latest__meta';
        meta.appendChild(time);
        if (opts && opts.showAuthor && item.author) {
          var sep = document.createElement('span');
          sep.className = 'vn-latest__meta-sep';
          sep.setAttribute('aria-hidden', 'true');
          sep.textContent = '·';
          meta.appendChild(sep);
          var who = document.createElement('span');
          who.className = 'vn-latest__author';
          who.textContent = 'by ' + item.author;
          meta.appendChild(who);
        }
        body.appendChild(meta);
      }
    }

    a.appendChild(body);
    li.appendChild(a);
    return li;
  }

  /** A FontAwesome glyph, decorative: it repeats the text beside it. */
  function icon(name) {
    var i = document.createElement('i');
    i.className = 'fa ' + name;
    i.setAttribute('aria-hidden', 'true');
    return i;
  }

  function render(section, items) {
    var grid = section.querySelector('[data-vn-grid]');
    if (!grid) return false;

    var blogHost = section.getAttribute('data-blog-host') || '';
    var compact = section.classList.contains('vn-latest--compact');
    var opts = {
      // Draw the meta line the way the native blog widget drew it.
      nativeMeta: section.hasAttribute('data-vn-native-meta'),
      showAuthor: section.getAttribute('data-vn-author') === 'true'
    };
    var frag = document.createDocumentFragment();
    var built = 0;

    for (var i = 0; i < items.length; i++) {
      var card = buildCard(items[i], blogHost, compact, opts);
      if (card) {
        frag.appendChild(card);
        built++;
      }
    }
    if (!built) return false;

    // Replace wholesale: a slow second response can never double the row.
    grid.textContent = '';
    grid.appendChild(frag);
    section.setAttribute('aria-busy', 'false');
    section.setAttribute('data-vn-state', 'ready');
    startCarousel(section, grid);
    return true;
  }

  /*
   * Turn a rendered grid into the theme's carousel.
   *
   * Only for sections marked data-vn-owl — the homepage row stays a static
   * grid. The theme initialises Owl on DOM ready, long before this feed
   * resolves, so a section filled afterwards has to ask for it. Options are
   * read from the same data-* attributes the theme's own initialiser uses, so
   * a sidebar carousel behaves like every other carousel on the page.
   *
   * Missing jQuery or Owl is not an error: the cards are already in the DOM
   * and simply stay a list, which is a perfectly good sidebar.
   */
  function startCarousel(section, grid) {
    if (!section.hasAttribute('data-vn-owl')) return;
    var $ = window.jQuery;
    if (!$ || !$.fn || !$.fn.owlCarousel) return;

    var $grid = $(grid);
    // A second render must not stack a carousel on top of a carousel.
    if ($grid.hasClass('owl-loaded')) {
      $grid.trigger('destroy.owl.carousel');
      $grid.removeClass('owl-loaded owl-hidden').find('.owl-stage-outer').children().unwrap();
    }

    var num = function (name, fallback) {
      var v = parseInt(grid.getAttribute(name), 10);
      return isNaN(v) ? fallback : v;
    };
    var flag = function (name) {
      return grid.getAttribute(name) === 'true';
    };

    try {
      $grid.owlCarousel({
        items: 1,
        loop: true,
        nav: flag('data-nav'),
        dots: flag('data-dots'),
        margin: num('data-margin', 0),
        autoplay: flag('data-autoplay'),
        autoplayTimeout: num('data-autospeed', 10000),
        autoplayHoverPause: true,
        smartSpeed: num('data-speed', 300),
        navText: ['<i class="fa fa-angle-left"></i>', '<i class="fa fa-angle-right"></i>'],
        navClass: ['owl-prev', 'owl-next']
      });
    } catch (e) {
      /* A carousel that will not start leaves a readable list behind. */
    }
  }

  function giveUp(section) {
    // The storefront renders as though this section had never been there.
    if (section && section.parentNode) section.parentNode.removeChild(section);
  }

  function load(section) {
    var endpoint = section.getAttribute('data-feed');
    var count = parseInt(section.getAttribute('data-count'), 10) || 4;
    if (!endpoint) return giveUp(section);

    var url;
    try {
      url = new URL(endpoint);
      url.searchParams.set('count', String(count));
    } catch (e) {
      return giveUp(section);
    }

    var cacheKey = url.href;
    var cached = readCache(cacheKey);
    if (cached && render(section, cached)) return;

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, TIMEOUT_MS);

    fetch(url.href, {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('status ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.items) || !data.items.length) {
          throw new Error('empty feed');
        }
        writeCache(cacheKey, data.items);
        if (!render(section, data.items)) throw new Error('nothing renderable');
      })
      .catch(function () {
        giveUp(section);
      })
      .then(function () {
        clearTimeout(timer);
      });
  }

  function init() {
    var sections = document.querySelectorAll('[data-vn-latest]');
    for (var i = 0; i < sections.length; i++) load(sections[i]);
  }

  /*
   * Two sections now use this file — the homepage row and the sidebar list —
   * and each carries its own <script src>. Identical sources are fetched once
   * but EXECUTED once per tag, so without this guard every section would be
   * loaded twice and fetch the feed twice. init() itself already covers every
   * [data-vn-latest] on the page, so the first run is the only one needed.
   */
  if (window.__vnLatestArticlesInit) return;
  window.__vnLatestArticlesInit = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor rebuilds a section without reloading the page.
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector ? e.target.querySelector('[data-vn-latest]') : null;
    if (s) load(s);
  });
})();
